import {
  type IContactNumber,
  SMS_INTEGRATION_REGION_CODE_MAP,
  type SmsIntegrationOption,
  SUPPORTED_REGION_CODE_MAP,
} from '@principle-theorem/principle-core/interfaces';
import { type Region } from '@principle-theorem/shared';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { formatPhoneNumber } from './patient-contact-details';

const phoneNumberCharactersPattern = /^[\d\s()+\-.]+$/;

/**
 * Each check answers a different question, so choose by what the number is
 * for: `isComplete` for a number someone can be reached on, the
 * `TextableMobile` methods for one the practice's SMS provider can text.
 */
export class PhoneNumber {
  /**
   * Landlines pass on purpose: this rejects junk, not numbers that can't be
   * texted. awesome-phonenumber ignores letters, so `0412 abc 678` would
   * otherwise pass.
   */
  static isComplete(phoneNumber: string, region: Region): boolean {
    if (!phoneNumberCharactersPattern.test(phoneNumber)) {
      return false;
    }
    return parsePhoneNumber(phoneNumber, {
      regionCode: SUPPORTED_REGION_CODE_MAP[region],
    }).valid;
  }

  /**
   * The SMS provider's regions decide which mobiles count, not the practice's
   * region, and with no provider nothing can be texted.
   */
  static isTextableMobile(
    phoneNumber: string,
    smsProvider?: SmsIntegrationOption
  ): boolean {
    if (!smsProvider) {
      return false;
    }
    const textableRegionCodes = SMS_INTEGRATION_REGION_CODE_MAP[smsProvider];
    return textableRegionCodes.some((defaultRegionCode) => {
      const parsed = parsePhoneNumber(phoneNumber, {
        regionCode: defaultRegionCode,
      });
      return (
        !!parsed.typeIsMobile &&
        textableRegionCodes.some(
          (textableRegionCode) => textableRegionCode === parsed.regionCode
        )
      );
    });
  }

  static hasTextableMobile(
    contactNumbers: IContactNumber[] = [],
    smsProvider?: SmsIntegrationOption
  ): boolean {
    return contactNumbers.some((contactNumber) =>
      PhoneNumber.isTextableMobile(contactNumber.number, smsProvider)
    );
  }

  /**
   * Returns the first textable mobile formatted for the practice's region by
   * `formatPhoneNumber`, not as it was entered.
   */
  static findTextableMobile(
    contactNumbers: IContactNumber[] = [],
    region: Region,
    smsProvider?: SmsIntegrationOption
  ): string | undefined {
    const mobile = contactNumbers.find((contactNumber) =>
      PhoneNumber.isTextableMobile(contactNumber.number, smsProvider)
    );
    return mobile ? formatPhoneNumber(mobile.number, region) : undefined;
  }
}
